import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Divider, Grid, styled, Typography } from '@mui/material';
import { format, parse, isValid } from 'date-fns';
import { round } from 'lodash';
import PageContainer from 'components/PageContainer';
import InvestorSearch from './InvestorSearch';
import InvestorWithdrawalForm from './InvestorWithdrawalForm';
import QueuedWithdrawalsTable from './QueuedWithdrawalsTable';
import { useLocation } from 'react-router-dom';
import AlertDialog from 'components/AlertDialog';
import { useGetRole } from 'hooks/ui/useGetRole';
import { FirmSelector } from 'components/FirmSelector';
import FullPageSuccess from 'components/FullPageSuccess';
import { WITHDRAWALS } from 'adminConstants/routes';
import FieldRow from 'components/FormElements/FieldRow';
import { useDisclosure } from 'further-ui/hooks';
import NoBankAccountWithdrawalModal from './NoBankAccountWithdrawalModal';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import PageSection from 'components/PageSection';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import { useCreateWithdrawal } from 'hooks/data/withdrawal/useWithdrawal';
import { AppRadioButton, AppDatePicker } from 'components/FormElements';
import Heading from 'components/Heading';
import Spacing from 'components/Spacing';
import { useFunds } from 'hooks/data/fund/useFunds';
import { useSortTranches } from 'hooks/ui/useSortTranches';
import TrancheMultiSelect from 'components/TrancheMultiSelect';
import { CalendarDay, pluralize } from 'further-ui/utils';
import trpcApi from 'lib/trpcClient';
import downloadFile from 'utils/downloadFile';
import { useNotification } from 'hooks/ui/useNotification';
import { Api } from 'further-types/withdrawal';
import { v4 } from 'uuid';

import type {
  InvestorDetails,
  QueuedWithdrawal,
} from 'hooks/data/withdrawal/types';
import ButtonAsText from 'components/ButtonAsText';

const loadExcelJs = async () => (await import('exceljs')).default;
type InvalidInvestor = NonNullable<
  Api.UploadWithdrawalFileResponse['invalidInvestors']
>[number];

type UploadedWithdrawal = NonNullable<
  Api.UploadWithdrawalFileResponse['validWithdrawals']
>[number];

enum InputMethod {
  Individual = 'individual',
  Bulk = 'bulk',
}

const RightAligned = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-end',
}));

const RequestWithdrawals: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [investorIdInWithdrawalForm, setInvestorIdInWithdrawalForm] = useState(
    query.get('investorId') || null,
  );
  const [queuedWithdrawals, setQueuedWithdrawals] = useState<
    Array<QueuedWithdrawal>
  >([]);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // firm ID is required so that we retrieve the selected investors correct cash balance.
  // If user is a super admin they'll need to first select a firm from the dropdown
  const { isSuperAdmin, firmId: usersFirmId } = useGetRole();
  const [firmId, setFirmId] = useState(usersFirmId ?? '');
  const [isSubmittingWithdrawals, setIsSubmittingWithdrawals] = useState(false);
  const {
    isOpen: shouldDisplayPrompt,
    onOpen: displayPrompt,
    onClose: hidePrompt,
  } = useDisclosure();
  const inputFile = useRef<HTMLInputElement>(null);
  const [withdrawalDate, setWithdrawalDate] = useState<Date | null>(new Date());
  const [selectedTrancheIds, setSelectedTrancheIds] = useState<Array<string>>(
    [],
  );
  const [isTemplateDownloaded, setIsTemplateDownloaded] = useState(false);
  const [wasFileDownloaded, setWasFileDownloaded] = useState(false);
  const [invalidInvestors, setInvalidInvestors] = useState<
    Array<InvalidInvestor>
  >([]);
  const initialValue = {
    inputMethod: InputMethod.Individual,
  };
  const notification = useNotification();
  const apiUtils = trpcApi.useUtils();

  const [value, setValue] = useState(initialValue);

  const { tranches } = useFunds({
    firmId: firmId ?? undefined,
    leanResponse: true,
  });
  const sortedTranches = useSortTranches(tranches?.data);
  const tranchesArray = useMemo(() => sortedTranches ?? [], [sortedTranches]);

  const createWithdrawal = useCreateWithdrawal({
    onSuccess: () => {
      setShowSuccess(true);
      setIsSubmittingWithdrawals(false);
    },
    onError: () => {
      setIsSubmittingWithdrawals(false);
    },
  });

  const createWithdrawalUploadFile =
    trpcApi.withdrawals.createWithdrawalUploadFile.useMutation();
  const validateBulkWithdrawals =
    trpcApi.withdrawals.validateBulkWithdrawals.useMutation();
  const validateIndividualWithdrawal =
    trpcApi.withdrawals.validateIndividualWithdrawal.useMutation();

  // Reset template state when form parameters change
  useEffect(() => {
    setIsTemplateDownloaded(false);
    setWasFileDownloaded(false);
  }, [selectedTrancheIds, withdrawalDate, firmId]);

  const handleFileDownload = async () => {
    if (!firmId) {
      notification.error('Please select a firm first.');
      return;
    }

    const trancheIdsToSend = !selectedTrancheIds.length
      ? undefined
      : selectedTrancheIds;

    const res = await createWithdrawalUploadFile.mutateAsync({
      firmId,
      withdrawalDate: withdrawalDate
        ? new CalendarDay(withdrawalDate)
        : undefined,
      trancheIds: trancheIdsToSend,
    });

    const byteCharacters = atob(res.fileBase64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadFile(blob, res.filename || 'withdrawals.xlsx');
    setIsTemplateDownloaded(true);
    setWasFileDownloaded(true);
  };

  const handleTemplateReset = () => {
    setIsTemplateDownloaded(false);
  };

  const parseWithdrawalDate = (
    dateString: string,
    fallbackDate: Date,
  ): Date => {
    try {
      const parsed = parse(dateString, 'dd/MM/yyyy', new Date());
      return isValid(parsed) ? parsed : fallbackDate;
    } catch {
      return fallbackDate;
    }
  };

  const fetchInvestorWithdrawal = async (
    withdrawal: UploadedWithdrawal,
    uploadDate: Date,
  ): Promise<QueuedWithdrawal | null> => {
    try {
      const investor = await apiUtils.investor.getInvestor.fetch({
        investorId: withdrawal.investorId,
      });

      if (!investor) return null;

      return {
        _id: investor._id,
        queueId: v4(),
        fullName: investor.fullName,
        cashBalance: investor.cashBalance,
        bankDetails: investor.bankDetails,
        withdrawalDetails: {
          netWithdrawalAmount: Number(withdrawal.amount),
          withdrawalFee: withdrawal.withdrawalFee ?? 0,
          totalWithdrawal: round(
            Number(withdrawal.amount) + Number(withdrawal.withdrawalFee ?? 0),
            2,
          ),
          withdrawalDate: parseWithdrawalDate(
            withdrawal.withdrawalDate ?? '',
            uploadDate,
          ),
          note: withdrawal.notes ?? '',
          exportedToCustodian: withdrawal.exportedToCustodian,
          isBulkUpload: true, // Withdrawals from file uploads are bulk uploads
          maximumWithdrawable: withdrawal.maximumWithdrawable,
        },
      };
    } catch (err) {
      console.error(`Failed to fetch investor ${withdrawal.investorId}:`, err);
      return null;
    }
  };

  const EXCEL_EPOCH = new Date(Date.UTC(1899, 11, 30));
  const MS_PER_DAY = 86400000;

  const normaliseExcelDate = (value: string | number | Date): Date | null => {
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    if (typeof value === 'number') {
      const date = new Date(EXCEL_EPOCH.getTime() + value * MS_PER_DAY);
      return isValid(date) ? date : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const parsed = parse(trimmed, 'dd/MM/yyyy', new Date());
      if (isValid(parsed)) return parsed;

      // Fallback to native Date parsing
      const fallback = new Date(trimmed);
      return isValid(fallback) ? fallback : null;
    }

    return null;
  };

  const processValidWithdrawals = async (
    validWithdrawals: Array<UploadedWithdrawal>,
    uploadDate: Date,
  ) => {
    const withdrawalPromises = validWithdrawals.map((withdrawal) =>
      fetchInvestorWithdrawal(withdrawal, uploadDate),
    );

    const results = await Promise.all(withdrawalPromises);
    return results.filter((w): w is QueuedWithdrawal => w !== null);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files?.length) return [];

    try {
      const file = files[0];
      const ExcelJS = await loadExcelJs();
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const withdrawalRows: Array<{
        systemInvestorId: string | number;
        netWithdrawalAmount: string | number;
        withdrawalAmount: string | number;
        withdrawalDate: CalendarDay;
        withdrawalFee?: string | number;
        notes?: string;
        exportStatus: string;
      }> = [];

      let hasValidationError = false;

      // Parse Excel file - skip rows 1-6 (title, notes, empty row, and header row)
      // Row 1: Title
      // Row 2-4: Notes
      // Row 5: Empty
      // Row 6: Headers
      // Row 7+: Data
      // Column order:
      // Column A (1): Investor
      // Column B (2): Email
      // Column C (3): Bank details
      // Column D (4): Cash balance
      // Column E (5): Withdrawal date
      // Column F (6): Net withdrawal amount
      // Column G (7): Withdrawal fee
      // Column H (8): Total withdrawal (formula, read-only)
      // Column I (9): Notes
      // Column J (10): Export status
      // Column K (11): System investor ID
      workbook.worksheets.forEach((sheet) => {
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber <= 6) return;

          // Skip empty rows
          const systemInvestorId = row.getCell(11).value; // Column K (11)
          if (!systemInvestorId) return;

          const netWithdrawalAmount = row.getCell(6).value; // Column F (6)
          const withdrawalDate = row.getCell(5).value; // Column E (5)
          const withdrawalFee = row.getCell(7).value; // Column G (7)
          const notes = row.getCell(9).value; // Column I (9)
          const exportStatus = row.getCell(10).value; // Column J (10)

          // Handle formula results (formulas return { result: value })
          const getCellValue = (value: unknown) => {
            if (value === null || value === undefined) return undefined;
            // @ts-expect-error - ExcelJS formula results have a result property
            return value?.result ?? value;
          };

          const systemInvestorIdValue = getCellValue(systemInvestorId);
          if (!systemInvestorIdValue) {
            hasValidationError = true;
            notification.error(
              'System investor ID is required for all rows. Please ensure all rows have a system investor ID.',
            );
            return;
          }

          const netWithdrawalAmountValue = getCellValue(netWithdrawalAmount);
          const withdrawalFeeValueRaw = getCellValue(withdrawalFee);

          const withdrawalFeeValue = !withdrawalFeeValueRaw
            ? undefined
            : Number(withdrawalFeeValueRaw);

          // Validate that fee is a valid number if provided (not NaN)
          if (
            withdrawalFeeValue !== undefined &&
            Number.isNaN(withdrawalFeeValue)
          ) {
            hasValidationError = true;
            notification.error(
              `Invalid withdrawal fee: "${withdrawalFeeValueRaw}". Please enter a valid number or leave the fee field empty.`,
            );
            return;
          }

          if (
            !netWithdrawalAmountValue ||
            Number(netWithdrawalAmountValue) === 0
          ) {
            if (withdrawalFeeValue !== undefined && withdrawalFeeValue !== 0) {
              hasValidationError = true;
              notification.error(
                'Withdrawal fees cannot be provided without a net withdrawal amount. Please enter an amount for any row with a fee.',
              );
            }
            return;
          }

          const validWithdrawalAmount = Number(netWithdrawalAmountValue);

          // Validate that amount is a valid number (not NaN)
          if (Number.isNaN(validWithdrawalAmount)) {
            hasValidationError = true;
            notification.error(
              `Invalid net withdrawal amount: "${netWithdrawalAmountValue}". Please enter a valid number.`,
            );
            return;
          }

          const rawWithdrawalDateValue = getCellValue(withdrawalDate);
          if (!rawWithdrawalDateValue) {
            hasValidationError = true;
            notification.error(
              'Withdrawal date is required for all rows with withdrawal amounts. Please ensure all rows have a withdrawal date.',
            );
            return;
          }

          const normalisedDate = normaliseExcelDate(rawWithdrawalDateValue);
          if (!normalisedDate || !isValid(normalisedDate)) {
            hasValidationError = true;
            notification.error(
              `Invalid withdrawal date: "${rawWithdrawalDateValue}". Please ensure all rows have a valid withdrawal date.`,
            );
            return;
          }

          const exportStatusValue = getCellValue(exportStatus)?.toString();
          if (!exportStatusValue) {
            hasValidationError = true;
            notification.error(
              'Export status is required for all rows with withdrawal amounts. Please ensure all rows have an export status.',
            );
            return;
          }

          withdrawalRows.push({
            systemInvestorId: systemInvestorIdValue,
            netWithdrawalAmount: validWithdrawalAmount,
            withdrawalAmount: validWithdrawalAmount,
            withdrawalDate: new CalendarDay(normalisedDate),
            withdrawalFee: withdrawalFeeValueRaw,
            notes: getCellValue(notes)?.toString(),
            exportStatus: exportStatusValue,
          });
        });
      });

      if (hasValidationError) {
        return [];
      }

      if (!withdrawalRows.length) {
        notification.error('No data rows found in the uploaded file.');
        return [];
      }

      const result = await validateBulkWithdrawals.mutateAsync({
        withdrawals: withdrawalRows,
        firmId,
      });

      // Handle invalid investors
      if (result.invalidInvestors?.length) {
        setInvalidInvestors(result.invalidInvestors);
        return [];
      }

      setInvalidInvestors([]);

      // Process valid withdrawals
      if (!result.validWithdrawals?.length) {
        notification.error('No valid withdrawals found in the uploaded file.');
        return [];
      }

      const uploadDate = withdrawalDate ?? new Date();
      const queuedWithdrawalsToAdd = await processValidWithdrawals(
        result.validWithdrawals,
        uploadDate,
      );

      if (!queuedWithdrawalsToAdd.length) {
        notification.error('Failed to fetch investor details for withdrawals.');
        return [];
      }

      notification.success(
        `${queuedWithdrawalsToAdd.length} withdrawal(s) added to queue.`,
      );
      return queuedWithdrawalsToAdd;
    } catch (err: unknown) {
      notification.error(
        err instanceof Error
          ? err.message
          : 'Error when uploading withdrawals.',
      );
      return [];
    }
  };

  const handleInputMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const method: InputMethod =
      e.target.value === InputMethod.Bulk
        ? InputMethod.Bulk
        : InputMethod.Individual;
    setValue({ ...value, inputMethod: method });
    if (method !== InputMethod.Bulk) {
      setSelectedTrancheIds([]);
      setIsTemplateDownloaded(false);
      setWasFileDownloaded(false);
    }
  };

  const handleUploadWithdrawalFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsProcessingUpload(true);
    try {
      const newWithdrawals = await handleFileUpload(event);
      if (newWithdrawals.length > 0) {
        // Replace existing withdrawals for the same investors
        const investorIdsToReplace = new Set(newWithdrawals.map((w) => w._id));
        const existingWithdrawals = queuedWithdrawals.filter(
          (w) => !investorIdsToReplace.has(w._id),
        );
        setQueuedWithdrawals([...existingWithdrawals, ...newWithdrawals]);
      }
    } finally {
      setIsProcessingUpload(false);
    }
  };

  const addInvestorToWithdrawalQueue = useCallback(
    (
      investor: InvestorDetails,
      withdrawalDetails: {
        netWithdrawalAmount: number;
        totalWithdrawal: number;
        withdrawalFee: string;
        withdrawalDate: Date;
        note: string;
        attachment?: {
          key: string;
        };
        maximumWithdrawable?: number;
      },
    ) => {
      setQueuedWithdrawals((prev) => [
        ...prev,
        {
          ...investor,
          queueId: v4(),
          withdrawalDetails: {
            ...withdrawalDetails,
            withdrawalFee: Number(withdrawalDetails.withdrawalFee),
            netWithdrawalAmount: withdrawalDetails.netWithdrawalAmount,
            totalWithdrawal: withdrawalDetails.totalWithdrawal,
            exportedToCustodian: false, // Individual withdrawals default to not exported
            maximumWithdrawable: withdrawalDetails.maximumWithdrawable,
          },
        },
      ]);
    },
    [],
  );

  const removeWithdrawalFromQueue = useCallback((queueId: string) => {
    setQueuedWithdrawals((prev) => prev.filter((qw) => qw.queueId !== queueId));
  }, []);

  const checkAndSubmitWithdrawalRequests = async () => {
    if (
      queuedWithdrawals.some(
        (queuedWithdrawal) =>
          !queuedWithdrawal.bankDetails?.bankAccountNumber &&
          !queuedWithdrawal.bankDetails?.bankSortCode &&
          !queuedWithdrawal.bankDetails?.iban,
      )
    ) {
      displayPrompt();
    } else {
      await submitWithdrawalRequests();
    }
  };

  const submitWithdrawalRequests = async () => {
    hidePrompt();
    setIsSubmittingWithdrawals(true);
    const requestBody = queuedWithdrawals.map((qw) => ({
      investorId: qw._id,
      firmId,
      amount: Number(qw.withdrawalDetails.netWithdrawalAmount),
      withdrawalFee: Number(qw.withdrawalDetails.withdrawalFee),
      withdrawalDate: format(qw.withdrawalDetails.withdrawalDate, 'dd/MM/yyyy'),
      notes: qw.withdrawalDetails.note,
      attachment: qw.withdrawalDetails.attachment?.key,
      exportedToCustodian: qw.withdrawalDetails.exportedToCustodian,
      isBulkUpload: qw.withdrawalDetails.isBulkUpload ?? false,
    }));
    createWithdrawal.mutate({ withdrawals: requestBody });
  };

  if (showSuccess) {
    return (
      <FullPageSuccess
        title="Your withdrawal requests have been successfully processed"
        additionalText="To export any pending requests to your custodian, please go to the Withdrawal Record by clicking on the button below."
        proceedText="Review Withdrawals"
        proceedLink={WITHDRAWALS}
      />
    );
  }

  return (
    <PageContainer heading="Withdrawals: Bulk withdrawal request">
      <PageContent>
        <PageSection title="Add a new withdrawal request" withSpacing>
          <RecordView size="small">
            {isSuperAdmin && (
              <FieldRow>
                <FirmSelector
                  placeholder="Select a firm"
                  firmId={firmId}
                  onChange={(selectedFirmId) => {
                    setFirmId(selectedFirmId);
                    setQueuedWithdrawals([]);
                    setSelectedTrancheIds([]);
                  }}
                />
              </FieldRow>
            )}
            <FieldRow title="Withdrawal input method" topTitle>
              <AppRadioButton
                required
                key="individual"
                name="inputMethod"
                label="Process single withdrawal"
                value={InputMethod.Individual}
                checked={value.inputMethod === InputMethod.Individual}
                onChange={handleInputMethodChange}
              />
              <AppRadioButton
                key="bulk"
                name="inputMethod"
                label="Bulk withdrawal import"
                value={InputMethod.Bulk}
                checked={value.inputMethod === InputMethod.Bulk}
                onChange={handleInputMethodChange}
              />
            </FieldRow>
            {firmId && value.inputMethod === InputMethod.Bulk && (
              <>
                <FieldRow title="Tranche(s)" centerTitle>
                  <TrancheMultiSelect
                    tranches={tranchesArray}
                    handleChangeSelectedTranches={setSelectedTrancheIds}
                    value={selectedTrancheIds}
                    selectAllLabel="All tranches"
                    disabled={tranches.isLoading}
                  />
                </FieldRow>
                <FieldRow title="Withdrawal date" centerTitle>
                  <AppDatePicker
                    value={withdrawalDate}
                    onChange={(date) => setWithdrawalDate(date)}
                    disableFuture
                    placeholder="Select withdrawal date"
                  />
                </FieldRow>
                <FieldRow>
                  <ButtonGroup>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        setIsTemplateDownloaded(true);
                        setWasFileDownloaded(false);
                      }}
                      disabled={!firmId}
                    >
                      Skip
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={handleFileDownload}
                      loading={createWithdrawalUploadFile.isPending}
                      disabled={!firmId || !selectedTrancheIds.length}
                    >
                      Download withdrawal import sheet
                    </Button>
                  </ButtonGroup>
                </FieldRow>
                {isTemplateDownloaded && (
                  <FieldRow>
                    <Typography variant="body2">
                      Please check your download folder for your withdrawal
                      import sheet, which enables you to add withdrawals for
                      investors within your chosen tranche(s). Please fill in
                      the required columns and upload the sheet here. Form
                      didn't download?{' '}
                      <ButtonAsText
                        disabled={createWithdrawalUploadFile.isPending}
                        onClick={() => {
                          if (wasFileDownloaded) {
                            // They actually downloaded, so download again
                            handleFileDownload();
                          } else {
                            // They skipped, so reset to show download form
                            handleTemplateReset();
                          }
                        }}
                      >
                        Click here to try again.
                      </ButtonAsText>
                    </Typography>
                  </FieldRow>
                )}
                {isTemplateDownloaded && (
                  <FieldRow>
                    <RightAligned>
                      <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => inputFile.current?.click()}
                        loading={
                          createWithdrawalUploadFile.isPending ||
                          validateBulkWithdrawals.isPending ||
                          isProcessingUpload
                        }
                        disabled={
                          invalidInvestors.length > 0 || isProcessingUpload
                        }
                      >
                        {isProcessingUpload ? 'Processing' : 'Upload'}
                        <input
                          name="createFeesFile"
                          type="file"
                          ref={inputFile}
                          hidden
                          onChange={handleUploadWithdrawalFile}
                          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                            e.currentTarget.value = '';
                          }}
                        />
                      </Button>
                    </RightAligned>
                  </FieldRow>
                )}
              </>
            )}
            {firmId && value.inputMethod === InputMethod.Individual && (
              <InvestorSearch
                onInvestorSelect={(id) => setInvestorIdInWithdrawalForm(id)}
                selectedInvestorId={investorIdInWithdrawalForm}
                queuedWithdrawals={queuedWithdrawals}
                firmId={firmId}
                disabled={validateIndividualWithdrawal.isPending}
              />
            )}
          </RecordView>
          <RecordView size="full">
            {firmId && value.inputMethod === InputMethod.Individual ? (
              <>
                <Divider />
                <InvestorWithdrawalForm
                  investorId={investorIdInWithdrawalForm}
                  validateIndividualWithdrawalMutation={
                    validateIndividualWithdrawal
                  }
                  addInvestorToWithdrawalQueue={(
                    investor,
                    withdrawalDetails,
                  ) => {
                    addInvestorToWithdrawalQueue(investor, withdrawalDetails);
                    setInvestorIdInWithdrawalForm(null);
                  }}
                  queuedWithdrawals={queuedWithdrawals}
                  firmId={firmId}
                />
              </>
            ) : (
              <>
                {invalidInvestors.length > 0 && (
                  <Grid size={{ md: 12 }}>
                    <Spacing>
                      <Heading variant="h3" noMargin>
                        Investors have insufficient funds available
                      </Heading>
                      <Typography variant="body1">
                        The following investors do not have sufficient funds
                        available for this withdrawal. To continue, please
                        either reduce the withdrawal amount, change the
                        withdrawal date or exclude these investors from the
                        withdrawal.
                      </Typography>
                      {invalidInvestors.map((investor, index) => (
                        <Typography variant="body1" key={index}>
                          {investor.investorName} ({investor.email})
                        </Typography>
                      ))}
                      <ButtonGroup>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => setInvalidInvestors([])}
                        >
                          Go back
                        </Button>
                      </ButtonGroup>
                    </Spacing>
                  </Grid>
                )}
              </>
            )}
            {queuedWithdrawals.length > 0 && (
              <PageSection title="Pending withdrawal requests" withSpacing>
                <QueuedWithdrawalsTable
                  queuedWithdrawals={queuedWithdrawals}
                  removeWithdrawalFromQueue={removeWithdrawalFromQueue}
                />
                <ButtonGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={checkAndSubmitWithdrawalRequests}
                    loading={isSubmittingWithdrawals}
                  >
                    Submit {pluralize('request', queuedWithdrawals.length)}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setIsCancelConfirmOpen(true)}
                    loading={isSubmittingWithdrawals}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </PageSection>
            )}
          </RecordView>
        </PageSection>
      </PageContent>
      <NoBankAccountWithdrawalModal
        isOpen={shouldDisplayPrompt}
        onConfirm={submitWithdrawalRequests}
        onCancel={hidePrompt}
      />
      <AlertDialog
        open={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
        onConfirm={() => {
          setQueuedWithdrawals([]);
          setIsCancelConfirmOpen(false);
        }}
        title="Cancel all pending withdrawal requests?"
        content="This will remove all pending withdrawal requests from the table. Are you sure you want to continue?"
        cancelBtnProps={{ variant: 'outlined', color: 'primary' }}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'OK',
        }}
      />
    </PageContainer>
  );
};

export default RequestWithdrawals;
